import { useCallback, useRef, useState } from 'react';
import { parseDateFields } from '../../hooks/http/useHttpFetch';
import { guardedPut } from '../../api/guardedPut';
import { FetchError } from '../../api/api';

export interface FormEntityUpsertDto {
  formTemplateId: number;
  version: number;
  statePath: string;
  data: any;
  isFinal: boolean;
  /**
   * duration in minutes spent on form state.
   */
  duration: number;
}

export function useUpsertFormEntityData(tableName: string) {
  const [upsertedEntityData, setUpsertedEntityData] = useState<any>();
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<FetchError>();

  // a response describes one entity table; carrying it across a table change
  // lets the next form inherit the previous row's id and study participant
  const tableNameRef = useRef(tableName);
  // monotonic so a response can be matched to the request that asked for it;
  // a table name cannot, since A -> B -> A returns to the same name
  const generationRef = useRef(0);
  if (tableNameRef.current !== tableName) {
    tableNameRef.current = tableName;
    generationRef.current += 1;
    setUpsertedEntityData(undefined);
    setError(undefined);
    // an abandoned request never settles this, so clear it here or the next
    // form opens on a stuck spinner
    setLoading(false);
  }

  const upsertEntityData = useCallback(
    async (formEntityUpsertDto: FormEntityUpsertDto) => {
      const requestTable = tableName;
      // A submit awaits plugin work (file uploads) before reaching here, so a
      // callback captured before a table change can still run. It must save to
      // its own table without making itself current, or its response would set
      // the new form's entity id.
      const stale = requestTable !== tableNameRef.current;
      // a later upsert supersedes this one, so an out-of-order response cannot
      // overwrite newer data or clear the newer request's spinner
      const generation = stale ? -1 : (generationRef.current += 1);
      if (!stale) setLoading(true);
      // guardedPut owns retries and never rejects — a timeout, network drop,
      // or non-JSON body surfaces as an error state instead of escaping the
      // hook and leaving the submit spinner stuck (CHLOE-MOBILE-APP-11)
      try {
        const { json, error } = await guardedPut(
          `forms/entities/${requestTable}`,
          formEntityUpsertDto
        );
        // A response outlives the form that asked for it. Applying a superseded
        // one would hand the current form another row's id, which
        // FormDataSource sends as data.id — the overwrite this exists to stop.
        if (stale || generationRef.current !== generation) return;
        if (error) {
          setUpsertedEntityData(undefined);
          setError(error);
        } else {
          setError(undefined); // a successful resubmit must clear a prior failure
          setUpsertedEntityData(json ? parseDateFields(json) : json);
        }
      } finally {
        // even an unexpected parse throw must never leave the spinner stuck
        if (!stale && generationRef.current === generation) setLoading(false);
      }
    },
    [tableName]
  );

  return {
    upsertedEntityData,
    isUpsertingData: loading,
    upsertingDataError: error,
    upsertEntityData
  };
}
